import { useState } from "react";
import { UserTable } from "@/components/user-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserActionsDropdown } from "@/components/user-actions-dropdown";
import type { organisationUsers } from "@shared/schema";

interface OrganisationUserTableProps {
  users: (typeof organisationUsers.$inferSelect)[];
  orgId: number;
  isGod: boolean;
  isOwner?: boolean;
  onAllocateUser: (orgId: number) => void;
  onRemoveUser: (orgId: number, userId: number, username: string) => void;
  onForceLogout: (userId: number) => void;
  onResetPassword: (userId: number, username: string) => void;
}

export function OrganisationUserTable({
  users,
  orgId,
  isGod,
  isOwner = false,
  onAllocateUser,
  onRemoveUser,
  onForceLogout,
  onResetPassword,
}: OrganisationUserTableProps) {
  const [userSearchTerm, setUserSearchTerm] = useState("");

  const filteredUsers = users?.filter((ou: any) =>
    ou.user.username.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  return (
    <div className="pt-4">
      <div className="flex items-center gap-2 mb-4">
        <Input
          placeholder="Search users..."
          value={userSearchTerm}
          onChange={(e) => setUserSearchTerm(e.target.value)}
          className="max-w-xs"
        />
        {userSearchTerm && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setUserSearchTerm("")}
            className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            Clear
          </Button>
        )}
        {isGod && (
          <Button 
            className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
            style={{
              boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
            size="sm" 
            onClick={() => onAllocateUser(orgId)}
          >
            Allocate User
          </Button>
        )}
      </div>
      <UserTable
        users={filteredUsers?.map((ou: any) => ou.user) as any}
        renderActions={(u: any) => {
          const ou = users.find((ou: any) => ou.user.id === u.id);
          return (
            <UserActionsDropdown
              user={u}
              onResetPassword={onResetPassword}
              onForceLogout={isGod || isOwner ? onForceLogout : undefined}
              onRemove={ou ? (userId, username) => onRemoveUser(orgId, userId, username) : undefined}
              showForceLogout={isGod || isOwner}
            />
          );
        }}
      />
    </div>
  );
}
